import { type ApiErrorEnvelope, isApiError } from "@compliance/domain";
import { apiErrorEnvelopeSchema, problemDetailsSchema } from "./schemas";

/**
 * Render an unknown thrown value as a user-facing message string.
 *
 * For an {@link ApiError} this surfaces the canonical `CODE: message` form —
 * the error-taxonomy code (API_CONTRACT §11) is meaningful to operators, so we
 * keep it visible. Any other thrown value falls back to its `Error.message`, or
 * its string form as a last resort (never `undefined`).
 *
 * Intended for notification/toast bodies. Inline alerts that additionally
 * render the `request_id` or branch on a specific error code keep their own
 * formatting rather than calling this.
 */
export function formatApiError(err: unknown): string {
  if (isApiError(err)) return `${err.code}: ${err.message}`;
  if (err instanceof Error) return err.message;
  return String(err);
}

/**
 * Did the API *rule on the session*, as opposed to failing to answer about it?
 *
 * This one question decides, in several places, whether a failure ends a session
 * or merely fails to renew it — so it lives here rather than beside any one
 * caller. It was previously answered by two separate predicates that disagreed:
 * one excluded `RATE_LIMITED`, the other did not, so a 429 during refresh ended
 * a session on one path and preserved it on the other.
 *
 * A 4xx is a verdict on the credential. A 5xx, a network drop, or a timeout is
 * no verdict at all, and neither is a 429 — which explicitly means "ask again
 * shortly", the same carve-out the identity retry guard makes.
 */
export function isSettledSessionRefusal(err: unknown): boolean {
  if (!isApiError(err)) return false;
  // "Ask again shortly" — the same carve-out the identity retry guard makes.
  if (err.code === "RATE_LIMITED") return false;
  // A stale CSRF token says nothing about the session behind it. Callers already
  // recover by re-reading `/session` and retrying once, but a second rotation in
  // that window produces a second CSRF_FAILED — and reading *that* as expiry
  // would end a session whose refresh cookie is perfectly good, over nothing more
  // than two tabs refreshing close together.
  if (err.code === "CSRF_FAILED") return false;
  return err.httpStatus >= 400 && err.httpStatus < 500;
}

/**
 * Normalise either error body the platform answers with into one envelope.
 *
 * Two shapes are in play, and which one arrives is not a property of the
 * endpoint:
 *
 *   - the compliance API's flat `{code, message, request_id}`;
 *   - RFC 9457 **Problem Details** (`{type, title, status, code, detail}`),
 *     which the browser-auth sub-app answers with — and, more consequentially,
 *     which the API edge answers with for anything the app never reached. An
 *     unmatched route is the common case: the edge, not FastAPI, writes that
 *     404.
 *
 * Parsing one with the other's schema loses the `code` the UI branches on and
 * reports `MALFORMED_ERROR` instead, which reads as "the server is broken"
 * rather than "that endpoint does not exist" — exactly how a dead per-row
 * Generate button presented for months. Accepting both here, in one place, is
 * what keeps the two clients (`lib/api.ts` and `auth/session.ts`) from drifting
 * apart on the question again.
 *
 * `requestId` comes from the `X-Request-Id` response header: Problem Details
 * carries no equivalent field, and the flat envelope's own `request_id` wins
 * when it has one.
 *
 * Returns `null` when the body is neither shape — the caller decides what to
 * say about a body it cannot read at all.
 */
export function errorEnvelopeFrom(body: unknown, requestId: string): ApiErrorEnvelope | null {
  const flat = apiErrorEnvelopeSchema.safeParse(body);
  if (flat.success) return flat.data;

  const problem = problemDetailsSchema.safeParse(body);
  if (problem.success) {
    return {
      code: problem.data.code,
      // RFC 9457: `title` describes the problem *type* and never varies between
      // occurrences, while `detail` is the per-occurrence text — so `detail` is
      // what a human should read. It may still be empty, and a title is better
      // than nothing.
      message: problem.data.detail || problem.data.title,
      request_id: requestId,
    };
  }
  return null;
}
